import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Collapse,
  IconButton,
  Box as MuiBox,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    defaultPrice: "",
    prices: [{ price: "", shop: "" }],
  });
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [shops, setShops] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredProduct, setFilteredProduct] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    setFilteredProduct(
      products.filter((product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [products, searchQuery]);
  const toggleExpandRow = (productId) => {
    setExpandedRows((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };
  useEffect(() => {
    const fetchShops = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/shops`);
        if (response.ok) {
          const data = await response.json();
          setShops(data);
        }
      } catch (err) {
        console.log("Error to fetch shops", err.message);
      }
    };
    fetchShops();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        console.log("our producat data is ", data);
      } else {
        console.error("Failed to fetch products.");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle Add/Edit
  const handleSave = async () => {
    try {
      // Ensure we are sending required fields
      if (!formData.name) {
        console.error("Missing required fields");
        return;
      }

      // Prepare payload for saving
      const payload = {
        name: formData.name.trim().toLowerCase(),
        defaultPrice: formData.defaultPrice,
        prices: formData.prices, // Send all prices array
      };

      const method = isEditing ? "PUT" : "POST";
      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}/product/${formData.productId}`
        : `${process.env.REACT_APP_API_URL}/product`;

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error("Failed to save product.");
        return;
      }

      // Fetch products again to update the list
      fetchProducts();
      setOpenFormDialog(false);
      setFormData({ name: "", description: "", prices: [] }); // Reset form
      setIsEditing(false); // Reset to Add mode
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  // Handle Delete with Confirmation
  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/product/${productToDelete._id}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        fetchProducts(); // Refresh products list
        setOpenDeleteDialog(false);
        setProductToDelete(null);
      } else {
        console.error("Failed to delete product.");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  // Open dialog for editing a product
  const handleEdit = (product) => {
    console.log("Editing product:", product);

    // Set form data for editing
    setFormData({
      productId: product._id,
      name: product.name || "",
      defaultPrice: product.defaultPrice || "",
      prices:
        product.prices.map((p) => ({
          _id: p._id,
          shop: p.shop?._id || "",
          price: p.price || "",
        })) || [], // Map price entries
    });

    setIsEditing(true); // Set to Edit mode
    setOpenFormDialog(true); // Open the dialog for editing
  };

  // Open delete confirmation dialog
  const confirmDelete = (product) => {
    setProductToDelete(product);
    setOpenDeleteDialog(true);
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const addShopPrice = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      prices: prevFormData.prices
        ? [...prevFormData.prices, { shop: "", price: "" }]
        : [{ shop: "", price: "" }],
    }));
  };

  const removeShopPrice = (index) => {
    const newPrices = formData.prices.filter((_, i) => i !== index);
    setFormData({ ...formData, prices: newPrices });
  };
  const handleShopPriceChange = (index, field, value) => {
    const updatedPrices = [...formData.prices];
    updatedPrices[index][field] = value; // Update either 'price' or 'shop'
    setFormData({ ...formData, prices: updatedPrices });
  };
  const productPagination = filteredProduct.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box padding={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Product Management
      </Typography>
      <Box display="flex" justifyContent="space-between" marginBottom={2}>
        <TextField
          placeholder="Search Product"
          label="Search Product"
          variant="outlined"
          margin="normal"
          sx={{
            width: "45%",
          }}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setFormData({ name: "", price: 0 });
            setIsEditing(false);
            setOpenFormDialog(true);
          }}
          sx={{ height: 45 }}
        >
          Add Product
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Items Name
              </TableCell>

              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Price
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {productPagination.map((product) => {
              const isExpanded = expandedRows.includes(product._id);
              const hasPrices = product?.prices?.length > 0;

              return (
                <React.Fragment key={product._id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        onClick={() => toggleExpandRow(product._id)}
                        size="small"
                      >
                        {isExpanded ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>{" "}
                      {product.name}
                    </TableCell>

                    <TableCell>{product.defaultPrice ?? "N/A"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleEdit(product)}
                        style={{ marginRight: "10px" }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => confirmDelete(product)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={4}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <MuiBox margin={2}>
                          <Typography variant="subtitle1" gutterBottom>
                            Shop Prices
                          </Typography>
                          {hasPrices ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Shop Name</TableCell>
                                  <TableCell>Price</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {product.prices.map((price, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      {price?.shop?.name || "N/A"}
                                    </TableCell>
                                    <TableCell>
                                      {price?.price ?? "N/A"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography>No shop-specific prices</Typography>
                          )}
                        </MuiBox>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredProduct.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Add/Edit Product Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)}>
        <DialogTitle>{isEditing ? "Edit Product" : "Add Product"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Default Price"
            name="defaultPrice"
            type="number"
            value={formData.defaultPrice}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          {/* Dynamic Shop-Price Inputs */}
          {formData.prices?.map((entry, index) => (
            <div
              key={index}
              style={{ display: "flex", gap: 10, marginBottom: 10 }}
            >
              <FormControl fullWidth>
                <InputLabel>Shop</InputLabel>
                <Select
                  value={entry.shop}
                  onChange={(e) =>
                    handleShopPriceChange(index, "shop", e.target.value)
                  }
                >
                  {shops.map((shop) => (
                    <MenuItem key={shop._id} value={shop._id}>
                      {shop.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Price"
                type="number"
                value={entry.price}
                onChange={(e) =>
                  handleShopPriceChange(index, "price", e.target.value)
                }
                fullWidth
              />
              <Button onClick={() => removeShopPrice(index)} color="secondary">
                ❌
              </Button>
            </div>
          ))}

          <Button onClick={addShopPrice} color="primary">
            ➕ Add Shop Price
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            {isEditing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the product:{" "}
            <strong>{productToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
